import ms from 'ms'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { ColumnConfig, VirtualizationConfig } from './DataGrid.types'
import { gridClassNames } from './utils'
import { useLocalStorageState } from './utils/useLocalStorageState'

export const GENERIC_DATA_GRID_ID = 'generic-data-grid'

export const useGridId = (id?: string) => {
  if (id === GENERIC_DATA_GRID_ID) {
    console.warn('Consider providing a stable gridId instead of generic id')
  }

  const generatedId = useMemo(() => Math.random().toString(36).substring(2), [])
  return id || `data-grid-${generatedId}`
}

const GRID_COLUMN_WIDTHS_VERSION = '1.0.1' // versioning to handle breaking changes and force default local storage on new version

export const useGridColumnWidths = (columns: ColumnConfig<any>[], gridId: string) => {
  const [columnGridWidths, setColumnGridWidths] = useLocalStorageState<string>(
    `columnGridWidths_${gridId}_${GRID_COLUMN_WIDTHS_VERSION}`,
    '',
    ms('5 days'),
  )

  const updateColumnGridWidths = useCallback((currentColumns: ColumnConfig<any>[]) => {
    const gridWidths = currentColumns.map(column => {
      if (column.width) {
        return column.width
      }

      if (column.minWidth) {
        return `minmax(${column.minWidth}, 1fr)`
      }

      return '1fr'
    })

    setColumnGridWidths(gridWidths.join(' '))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (columnGridWidths === '') {
      updateColumnGridWidths(columns)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns])

  return {
    columnGridWidths,
    updateColumnGridWidths,
  }
}

// Basic virtualization logic for big grids
// it is not rendering more than X first rows to speed-up rendering
// next rows are rendered on scroll
export const useRowsVirtualization = (
  gridId: string,
  resetDeps: unknown[],
  virtualizationConfig?: VirtualizationConfig,
  groupBorders?: number[],
) => {
  const [rowsToRender, setRowsToRender] = useState({
    start: 0,
    end: virtualizationConfig
      ? virtualizationConfig.initialVisibleRows + virtualizationConfig.scrollOffsetRows
      : 20,
  })
  useEffect(() => {
    if (virtualizationConfig) {
      setRowsToRender({ start: 0, end: virtualizationConfig.initialVisibleRows })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualizationConfig, ...resetDeps])

  useEffect(() => {
    if (!virtualizationConfig) {
      return
    }
    const gridWrapper = document.getElementById(gridId)
    const scrollWrapper = gridWrapper?.closest(`.${gridClassNames.scrollWrapper}`)
    if (!scrollWrapper) {
      return
    }
    const handleScroll = () => {
      const pointer =
        virtualizationConfig.initialVisibleRows +
        Math.ceil(scrollWrapper.scrollTop / virtualizationConfig.rowHeight)

      const groupHeaders =
        groupBorders && groupBorders.length > 0
          ? pointer > groupBorders[groupBorders.length - 1]
            ? groupBorders.length
            : groupBorders.findIndex(groupBorder => groupBorder > pointer)
          : 0

      const endIndex = pointer - groupHeaders

      setRowsToRender({
        start: Math.max(
          0,
          endIndex -
            virtualizationConfig.initialVisibleRows -
            virtualizationConfig.scrollOffsetRows,
        ),
        end: endIndex + virtualizationConfig.scrollOffsetRows,
      })
    }

    scrollWrapper.addEventListener('scroll', handleScroll)
    return () => scrollWrapper.removeEventListener('scroll', handleScroll)
  }, [gridId, virtualizationConfig, groupBorders])

  return { rowsToRender }
}
